import { useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { copyToClipboard, uuid } from '@/lib/utils';
import { toast } from 'sonner';

function nanoid(size = 21): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  const arr = crypto.getRandomValues(new Uint8Array(size));
  return Array.from(arr, (b) => alphabet[b % alphabet.length]).join('');
}

function ulid(): string {
  // Crockford-base32 ULID-ish encoder
  const enc = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const time = Date.now();
  let timeStr = '';
  let t = time;
  for (let i = 9; i >= 0; i--) {
    timeStr = enc[t % 32] + timeStr;
    t = Math.floor(t / 32);
  }
  const rand = crypto.getRandomValues(new Uint8Array(16));
  let r = '';
  for (let i = 0; i < 16; i++) r += enc[rand[i] % 32];
  return timeStr + r;
}

export function UuidGenerator() {
  const [count, setCount] = useState(8);
  const [type, setType] = useState<'uuid' | 'nanoid' | 'ulid'>('uuid');
  const [list, setList] = useState<string[]>(() => Array.from({ length: 8 }, () => uuid()));

  function regen(t = type, c = count) {
    const fn = t === 'uuid' ? uuid : t === 'nanoid' ? () => nanoid() : ulid;
    setList(Array.from({ length: c }, () => fn()));
  }

  return (
    <div>
      <ToolHeader
        toolId="uuid-generator"
        title="ID generator"
        description="Generate UUIDs (v4), nanoIDs, and ULIDs. Bulk generation with one-click copy."
      />

      <Tabs
        value={type}
        onValueChange={(v) => {
          setType(v as typeof type);
          regen(v as typeof type, count);
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="uuid">UUID v4</TabsTrigger>
            <TabsTrigger value="nanoid">nanoID</TabsTrigger>
            <TabsTrigger value="ulid">ULID</TabsTrigger>
          </TabsList>
          <div className="flex items-end gap-2">
            <Field label="Count" className="!mb-0">
              <Input
                type="number"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                min={1}
                max={200}
                className="!w-24"
              />
            </Field>
            <Button variant="primary" size="sm" onClick={() => regen()}>
              <RefreshCw size={11} />
              Regenerate
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                const ok = await copyToClipboard(list.join('\n'));
                if (ok) toast.success(`${list.length} IDs copied`);
              }}
            >
              <Copy size={11} />
              Copy all
            </Button>
          </div>
        </div>

        <TabsContent value={type}>
          <Card className="!p-0">
            <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
              {list.map((id, i) => (
                <div
                  key={`${id}-${i}`}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-surface-hover transition group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xs text-fg-subtle font-mono w-7 text-right shrink-0">
                      {i + 1}
                    </span>
                    <span className="font-mono text-sm truncate">{id}</span>
                  </div>
                  <button
                    onClick={async () => {
                      const ok = await copyToClipboard(id);
                      if (ok) toast.success('Copied');
                    }}
                    className="opacity-0 group-hover:opacity-100 transition size-7 rounded hover:bg-surface-hover text-fg-subtle hover:text-fg flex items-center justify-center shrink-0"
                  >
                    <Copy size={11} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default UuidGenerator;
